// Kawalan borang yang diterjemah sepenuhnya. Input asli pelayar (date/time/file) dan tooltip
// pengesahan pelayar memaparkan teks dalam bahasa PELAYAR, bukan bahasa laman — menyebabkan
// bahasa bercampur. Kawalan di sini menggantikannya dengan teks daripada kamus semasa.

import { html, esc } from './html.js';
import { t } from './i18n.js';
import { fmtDate } from './format.js';
import { modal, calendar } from './ui.js';

let uid = 0;
const nextId = (p) => `${p}-${++uid}`;

// ---------- Medan tarikh ----------
// Menghasilkan <input type="hidden"> (nilai YYYY-MM-DD) + butang yang membuka kalendar.
export function dateField(name, { value = '', min = '', label = '', required = false, placeholder, id } = {}) {
  const fid = id || nextId('df');
  return html`<span class="datefield" data-datefield data-min="${min}">
    <input type="hidden" name="${name}" value="${value}" ${required ? 'data-required="1"' : ''} id="${fid}-v">
    <button type="button" class="input datebtn" id="${fid}" aria-haspopup="dialog" ${label ? html`aria-label="${label}"` : ''} data-ph="${placeholder || t('control.pick_date')}">${value ? fmtDate(value) : placeholder || t('control.pick_date')}</button>
  </span>`;
}

export function setDateField(wrap, value) {
  const input = wrap.querySelector('input[type=hidden]');
  const btn = wrap.querySelector('button');
  input.value = value || '';
  btn.textContent = value ? fmtDate(value) : btn.dataset.ph;
  btn.classList.toggle('placeholder', !value);
  wrap.classList.remove('invalid');
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function openDatePicker(wrap) {
  const input = wrap.querySelector('input[type=hidden]');
  const today = document.documentElement.dataset.today || new Date().toISOString().slice(0, 10);
  const min = wrap.dataset.min || '1970-01-01';
  modal(t('control.pick_date'), html`<div id="dp"></div><div class="row" style="justify-content:space-between;margin-top:10px"><button type="button" class="btn btn-ghost btn-sm" id="dp-clear">${t('common.clear')}</button><button type="button" class="btn btn-ghost btn-sm" id="dp-close">${t('common.close')}</button></div>`, {
    onMount(body, close) {
      calendar(body.querySelector('#dp'), {
        mode: 'single', start: input.value || null, minDate: min, view: (input.value || (min > today ? min : today)).slice(0, 7), months: 1,
        onChange(d) { setDateField(wrap, d); close(); wrap.querySelector('button').focus(); },
      });
      body.querySelector('#dp-clear').onclick = () => { setDateField(wrap, ''); close(); };
      body.querySelector('#dp-close').onclick = () => close();
    },
  });
}

// ---------- Pilihan masa (gantian <input type="time">) ----------
export function timeSelect(name, value = '', { step = 30, required = true } = {}) {
  const opts = [];
  for (let m = 0; m < 24 * 60; m += step) opts.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  if (value && !opts.includes(value)) opts.push(value);
  return html`<select class="input" name="${name}" ${required ? 'required' : ''}>${opts.sort().map((o) => html`<option value="${o}" ${o === value ? 'selected' : ''}>${o}</option>`)}</select>`;
}

// ---------- Medan fail (gantian butang "Choose file" pelayar) ----------
export function fileField(name, { accept = '', multiple = false, required = false, label = '' } = {}) {
  const fid = nextId('ff');
  return html`<span class="filefield" data-filefield>
    <input class="sr" type="file" id="${fid}" name="${name}" ${accept ? html`accept="${accept}"` : ''} ${multiple ? 'multiple' : ''} ${required ? 'required' : ''}>
    <label for="${fid}" class="btn btn-ghost btn-sm">${label || (multiple ? t('control.choose_files') : t('control.choose_file'))}</label>
    <span class="small muted" data-fname>${t('control.no_file')}</span>
  </span>`;
}

// ---------- Pengesahan borang dalam bahasa laman ----------
function messageFor(el) {
  const v = el.validity;
  if (v.valueMissing) return t(el.type === 'file' ? 'validate.file_required' : el.tagName === 'SELECT' ? 'validate.select_required' : 'validate.required');
  if (v.typeMismatch) return t(el.type === 'email' ? 'validate.email' : el.type === 'url' ? 'validate.url' : 'validate.format');
  if (v.patternMismatch) return el.dataset.patternMsg || t('validate.format');
  if (v.tooShort) return t('validate.min_length', { n: el.minLength });
  if (v.tooLong) return t('validate.max_length', { n: el.maxLength });
  if (v.rangeUnderflow) return t('validate.min_value', { n: el.min });
  if (v.rangeOverflow) return t('validate.max_value', { n: el.max });
  if (v.stepMismatch || v.badInput) return t('validate.number');
  return t('validate.format');
}

function showFieldError(el, msg) {
  clearFieldError(el);
  const box = el.closest('.field, .datefield, .filefield, .seg') || el.parentElement;
  const p = document.createElement('p');
  p.className = 'errtext field-error';
  p.setAttribute('role', 'alert');
  p.textContent = msg;
  box.appendChild(p);
  el.setAttribute('aria-invalid', 'true');
}

function clearFieldError(el) {
  const box = el.closest('.field, .datefield, .filefield, .seg') || el.parentElement;
  box?.querySelectorAll('.field-error').forEach((n) => n.remove());
  el.removeAttribute('aria-invalid');
}

export function validateForm(form) {
  form.querySelectorAll('.field-error').forEach((n) => n.remove());
  form.querySelectorAll('[aria-invalid]').forEach((n) => n.removeAttribute('aria-invalid'));
  let first = null;
  for (const el of form.querySelectorAll('input, select, textarea')) {
    if (el.disabled) continue;
    if (el.type === 'hidden') {
      if (el.dataset.required && !el.value) {
        const btn = el.closest('.datefield')?.querySelector('button');
        if (btn) { showFieldError(btn, t('validate.date_required')); first ||= btn; }
      }
      continue;
    }
    if (!el.checkValidity()) {
      showFieldError(el, messageFor(el));
      first ||= el.type === 'file' ? el.closest('.filefield').querySelector('label') : el;
    }
  }
  if (first) first.focus();
  return !first;
}

// Pasang sekali: matikan tooltip pelayar dan sahkan sebelum pengendali submit halaman berjalan.
export function installControls(root = document) {
  const markForms = (node) => node.querySelectorAll?.('form').forEach((f) => { f.noValidate = true; });
  markForms(root);
  new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1) { if (n.tagName === 'FORM') n.noValidate = true; markForms(n); } }))).observe(document.body, { childList: true, subtree: true });

  // Fasa tangkap pada dokumen berjalan sebelum pengendali `onsubmit` borang.
  document.addEventListener('submit', (e) => {
    if (!validateForm(e.target)) { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('input', (e) => { if (e.target.getAttribute?.('aria-invalid') && e.target.checkValidity?.()) clearFieldError(e.target); });
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-datefield] > button');
    if (btn) openDatePicker(btn.parentElement);
  });
  document.addEventListener('change', (e) => {
    const f = e.target.closest?.('[data-filefield] input[type=file]');
    if (!f) return;
    const label = f.closest('[data-filefield]').querySelector('[data-fname]');
    label.textContent = !f.files.length ? t('control.no_file') : f.files.length === 1 ? f.files[0].name : t('control.files_selected', { n: f.files.length });
    clearFieldError(f);
  });
}

export { esc };
